import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { pqClient } from '@/features/prequalification/localControl/endpoints/pqClient'
import type { PqMarketCatalogueEntry } from '@/features/prequalification/localControl/types'
import type { UseQueryOptions } from '@/types/queries'

export const GET_LOCAL_PQ_MARKETS_API_ID = 'GET_LOCAL_PQ_MARKETS'

export type UseLocalPqMarketsQueryResult = Omit<UseQueryResult, 'data'> & {
  markets: PqMarketCatalogueEntry[] | null
}

/**
 * The catalogue only changes when PQM is deployed, so it is fetched once per page load.
 *
 * A failure raises no toast, unlike the other local-PQ queries. The resource-set list still
 * works without the catalogue, since its chips fall back to the market id, so a toast there
 * would report a problem the user cannot see. The details page shows its own inline error
 * with a retry. The global QueryCache `onError` still logs the failure.
 */
export function useLocalPqMarketsQuery(
  options?: UseQueryOptions<PqMarketCatalogueEntry[]>,
): UseLocalPqMarketsQueryResult {
  const { data, ...queryResult } = useQuery({
    staleTime: Infinity,
    ...options,
    queryFn: () => pqClient.listMarkets(),
    queryKey: [GET_LOCAL_PQ_MARKETS_API_ID],
  })

  return {
    ...queryResult,
    markets: data ?? null,
  }
}

export function useInvalidateLocalPqMarketsQuery() {
  const queryClient = useQueryClient()

  return () => queryClient.invalidateQueries({ queryKey: [GET_LOCAL_PQ_MARKETS_API_ID] })
}
